import React, { useEffect, useState, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { CirclePlus, DeleteIcon, Edit, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import axios from "axios";
import { toast } from "sonner";

const token = localStorage.getItem("adminToken");
const ITEMS_PER_PAGE = 10;
const Spinner = () => (
  <div className="inline-block w-5 h-5 border-4 border-t-transparent border-gray-600 rounded-full animate-spin"></div>
);

const Service = () => {
  const [formData, setFormData] = useState({
    category: "",
    subCategory: "",
    service: "",
    description: "",
    includes: "",
    excludes: "",
    price: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingService, setEditingService] = useState(null); // Track which service is being edited
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [updateFile, setUpdateFile] = useState(null);
  const [updateService, setUpdateService] = useState("");
  const [updateServiceDesc, setUpdateServiceDesc] = useState("");

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [servicesArray, setServicesArray] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter subcategories based on selected category
  const filteredSubCategories = subCategories.filter(
    (subcategory) => subcategory?.category?.name === formData?.category
  );

  // Fetch all categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(
        "https://wavetechservices.in/api/admin/services/service-categories",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCategories(res.data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    }
  }, []);

  // Fetch all subcategories
  const fetchSubCategories = useCallback(async () => {
    try {
      const res = await axios.get(
        "https://wavetechservices.in/api/user/subcategories",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSubCategories(res.data.data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      toast.error("Failed to fetch subcategories");
    }
  }, []);

  // Fetch all services and transform the data
  const fetchServices = useCallback(async () => {
    setIsLoading(true);
    try {
      const [servicesRes, subcategoriesRes] = await Promise.all([
        axios.get("https://wavetechservices.in/api/user/services", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get("https://wavetechservices.in/api/user/subcategories", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);
      console.log("servicesRes : " , servicesRes)
      const transformedServices = subcategoriesRes.data.data.flatMap(
        (serviceCategory) =>
          serviceCategory?.services?.map((service) => ({
            serviceId: service?._id,
            serviceName: service?.name,
            serviceDescription: service?.description,
            serviceIcon: service?.icon,

            // Sub-Category Details
            subCategoryId: service?.subCategory?._id,
            subCategoryName: service?.subCategory?.name,
            subCategoryImage: service?.subCategory?.image,
            subCategoryIsActive: service?.subCategory?.isActive,
            subCategoryCreatedAt: service?.subCategory?.createdAt,

            // Category Details
            categoryId: serviceCategory?.category?._id,
            categoryName: serviceCategory?.category?.name,
            categoryDescription: serviceCategory?.category?.description,
            categoryIcon: serviceCategory?.category?.icon,
            categoryIsActive: serviceCategory?.category?.isActive,
            categoryCreatedAt: serviceCategory?.category?.createdAt,

            // Other Details
            isActive: service?.isActive,
            createdAt: service?.createdAt,
          }))
      );

      setServicesArray(transformedServices);
      setFilteredServices(transformedServices);
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Failed to fetch services");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    fetchServices();
  }, [fetchCategories, fetchSubCategories, fetchServices]);

  // Filter services based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredServices(servicesArray);
      setCurrentPage(1);
    } else {
      const filtered = servicesArray.filter(
        (service) =>
          service.serviceName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          service.categoryName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          service.subCategoryName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          service.serviceDescription
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredServices(filtered);
      setCurrentPage(1);
    }
  }, [searchTerm, servicesArray]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file uploads with validation
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && !selectedFile.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setFile(selectedFile);
  };

  const handleUpdateFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && !selectedFile.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setUpdateFile(selectedFile);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!file) {
      toast.error("Please select an icon file");
      setIsSubmitting(false);
      return;
    }

    if (!formData.category || !formData.subCategory || !formData.service) {
      toast.error("Please fill all required fields");
      setIsSubmitting(false);
      return;
    }

    const selectedSubCategory = subCategories.find(
      (subCategory) => subCategory?.name === formData?.subCategory
    );

    if (!selectedSubCategory) {
      toast.error("Selected subcategory not found");
      setIsSubmitting(false);
      return;
    }

    const data = new FormData();
    data.append("category", formData.category);
    data.append("name", formData.service);
    data.append("description", formData.description || "N/A");
    data.append("subCategory", selectedSubCategory._id);
    data.append("basePrice", formData.price || "0");
    data.append("duration", "2");
    data.append("includes", formData.includes || "N/A");
    data.append("excludes", formData.excludes || "N/A");
    data.append("icon", file);
    data.append("categoryId", selectedSubCategory._id);

    try {
      await axios.post("https://wavetechservices.in/api/admin/service", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Service added successfully");
      setIsSubmitting(false);
      setOpenAdd(false);
      setFormData({
        category: "",
        subCategory: "",
        service: "",
        description: "",
        includes: "",
        excludes: "",
        price: "",
      });
      setFile(null);
      fetchServices();
    } catch (error) {
      console.error("Error:", error);
      setIsSubmitting(false);

      toast.error("Failed to add service");
    }
  };
 
  // Update your handleUpdate function:
  const handleUpdate = async (service) => {
    setIsSubmitting(true);
    if (!service?.serviceName) {
      toast.error("Please provide a service name");
      setIsSubmitting(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("name", service.serviceName);
    formDataToSend.append("description", service.serviceDescription || "");
    if (updateFile) formDataToSend.append("icon", updateFile);

    try {
      await axios.put(
        `https://wavetechservices.in/api/admin/service/${service.serviceId}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Service updated successfully");
      setIsSubmitting(false);
      setOpenEdit(false);
      setEditingService(null);
      setUpdateFile(null);
      fetchServices();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to update service");
      setIsSubmitting(false);
    }
  };
 

  const handleDelete = async () => { // Remove service parameter
    setIsSubmitting(true);
    if (!serviceToDelete) return;

    try {
      await axios.delete(
        `https://wavetechservices.in/api/admin/service/${serviceToDelete.serviceId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Service deleted successfully");
      setIsSubmitting(false);
      setOpenDelete(false);
      setServiceToDelete(null); // Clear the service to delete
      fetchServices();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to delete service");
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredServices.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  console.log("currentItems : ", currentItems);
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="px-[1vw] my-5">
      <div className="flex lg:justify-between lg:items-center flex-col lg:flex-row">
        <h1 className="text-2xl">Services</h1>
        <div className="flex lg:gap-4 items-center gap-2 my-3 lg:my-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search services..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <Dialog open={openAdd} onOpenChange={setOpenAdd}>
            <DialogTrigger asChild>
              <Button className="bg-black px-5 text-white h-9 hover:opacity-90 flex items-center gap-1">
                Add <CirclePlus className="size-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="lg:text-2xl text-xl">
                  Add Service
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4">
                  <Label htmlFor="category" className="w-44">
                    Category
                  </Label>
                  <Select
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        category: value,
                        subCategory: "",
                      })
                    }
                    value={formData.category}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category._id} value={category.name}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="subCategory" className="w-44">
                    Sub Category
                  </Label>
                  <Select
                    onValueChange={(value) =>
                      setFormData({ ...formData, subCategory: value })
                    }
                    value={formData.subCategory}
                    disabled={!formData.category}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredSubCategories.map((subCategory) => (
                        <SelectItem
                          key={subCategory._id}
                          value={subCategory.name}
                        >
                          {subCategory.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="service" className="w-44">
                    Service Name
                  </Label>
                  <Input
                    id="service"
                    name="service"
                    type="text"
                    value={formData.service}
                    onChange={handleChange}
                    className="flex-1"
                    required
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="description" className="w-44">
                    Description
                  </Label>
                  <Input
                    id="description"
                    name="description"
                    type="text"
                    value={formData.description}
                    onChange={handleChange}
                    className="flex-1"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="icon" className="w-44">
                    Icon
                  </Label>
                  <Input
                    id="icon"
                    type="file"
                    onChange={handleFileChange}
                    className="flex-1"
                    accept="image/*"
                    required
                  />
                </div>

                {/* <div className="flex items-center gap-4">
                  <Label htmlFor="price" className="w-44">
                    Price
                  </Label>
                  <Input
                    id="price"
                    name="price"
                    type="text"
                    value={formData.price}
                    onChange={handleChange}
                    className="flex-1"
                  />
                </div> */}
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  className="bg-black px-5 text-white h-9 hover:opacity-90 w-full flex items-center justify-center gap-2"
                  onClick={handleSubmit}
                  disabled={isSubmitting ||
                    !formData.category ||
                    !formData.subCategory ||
                    !formData.service ||
                    !file
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Spinner />
                      Adding...
                    </>
                  ) : (
                    "Add Service"
                  )}

                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Separator className="my-4" />

      <div className="overflow-x-auto lg:max-w-[1200px] max-w-[380px] md:max-w-[580px] lg:h-[65vh] h-[58vh] md:h-[70vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">Sl No.</TableHead>
              <TableHead className="min-w-[150px]">Category</TableHead>
              <TableHead className="min-w-[150px]">Sub Category</TableHead>
              <TableHead className="min-w-[250px]">Service Id</TableHead>
              <TableHead className="min-w-[150px]">Service</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Icon</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                   <div className="flex items-center justify-center gap-2">
                    <Spinner/>
                     Loading services...
                   </div>
                 
                </TableCell>
              </TableRow>
            ) : currentItems.length > 0 ? (
              currentItems.map((service, index) => (
                <TableRow key={`${service.serviceId}-${index}`}>
                  <TableCell>{indexOfFirstItem + index + 1}</TableCell>
                  <TableCell className="font-medium truncate max-w-[200px]">
                    {service.categoryName}
                  </TableCell>
                  <TableCell className="truncate max-w-[200px]">
                    {service.subCategoryName}
                  </TableCell>
                  <TableCell className="truncate max-w-[200px]">
                    {service.serviceId}
                  </TableCell>
                  <TableCell className="truncate max-w-[200px]">
                    {service.serviceName}
                  </TableCell>
                  <TableCell className="truncate max-w-[300px]">
                    {service.serviceDescription}
                  </TableCell>
                  <TableCell>
                    <img
                      src={`${service.serviceIcon}`}
                      alt={service.serviceName}
                      className="w-8 h-8 object-contain"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-4 items-center">
                      {/* <Dialog
                        open={openEdit}
                        onOpenChange={(open) => {
                          setOpenEdit(open);
                          if (open) {
                            setUpdateService(service.serviceName);
                            setUpdateServiceDesc(service.serviceDescription);
                          } else {
                            setUpdateService("");
                            setUpdateServiceDesc("");
                            setUpdateFile(null);
                          }
                        }}
                      >
                        <DialogTrigger>
                          <Edit className="text-blue-500 size-5 cursor-pointer hover:text-blue-700" />
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle className="lg:text-2xl text-xl">
                              Edit Service
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="flex items-center gap-4">
                              <Label htmlFor="edit-name" className="w-44">
                                Service Name
                              </Label>
                              <Input
                                id="edit-name"
                                type="text"
                                value={updateService}
                                onChange={(e) => setUpdateService(e.target.value)}
                                className="flex-1"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="edit-desc" className="w-44">
                                Description
                              </Label>
                              <Input
                                id="edit-desc"
                                type="text"
                                value={updateServiceDesc}
                                onChange={(e) => setUpdateServiceDesc(e.target.value)}
                                className="flex-1"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="edit-icon" className="w-44">
                                Icon
                              </Label>
                              <Input
                                id="edit-icon"
                                type="file"
                                onChange={handleUpdateFileChange}
                                className="flex-1"
                                accept="image/*"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              type="submit"
                              className="bg-black px-5 text-white h-9 hover:opacity-90 w-full"
                              onClick={() => handleUpdate(service)}
                              disabled={!updateService && !updateServiceDesc && !updateFile}
                            >
                              Save Changes
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog> */}

                      <Dialog
                        open={openEdit}
                        onOpenChange={(open) => {
                          setOpenEdit(open);
                          if (open) {
                            setEditingService(service); // Set the current service being edited
                          } else {
                            setEditingService(null); // Clear when closing
                          }
                        }}
                      >
                        <DialogTrigger>
                          <Edit className="text-blue-500 size-5 cursor-pointer hover:text-blue-700" />
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle className="lg:text-2xl text-xl">
                              Edit Service
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="flex items-center gap-4">
                              <Label htmlFor="edit-name" className="w-44">
                                Service Name
                              </Label>
                              <Input
                                id="edit-name"
                                type="text"
                                defaultValue={editingService?.serviceName || ""}
                                onChange={(e) =>
                                  setEditingService((prev) => ({
                                    ...prev,
                                    serviceName: e.target.value,
                                  }))
                                }
                                className="flex-1"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="edit-desc" className="w-44">
                                Description
                              </Label>
                              <Input
                                id="edit-desc"
                                type="text"
                                defaultValue={
                                  editingService?.serviceDescription || ""
                                }
                                onChange={(e) =>
                                  setEditingService((prev) => ({
                                    ...prev,
                                    serviceDescription: e.target.value,
                                  }))
                                }
                                className="flex-1"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="edit-icon" className="w-44">
                                Icon
                              </Label>
                              <Input
                                id="edit-icon"
                                type="file"
                                onChange={handleUpdateFileChange}
                                className="flex-1"
                                accept="image/*"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              type="submit"
                              className="bg-black px-5 text-white h-9 hover:opacity-90 w-full flex items-center justify-center gap-2"
                              onClick={() => handleUpdate(editingService)}
                              disabled={isSubmitting||!editingService?.serviceName}
                            >
                              {isSubmitting ? (<>
                              <Spinner/>
                              Updating...
                              </>):"Save Changes"

                              }
                              
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      {/* <Dialog open={openDelete} onOpenChange={setOpenDelete}>
                        <DialogTrigger>
                          <DeleteIcon className="text-red-500 size-5 cursor-pointer hover:text-red-700" />
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Confirm Deletion</DialogTitle>
                            <DialogDescription>
                              Are you sure you want to delete this service? This
                              action cannot be undone.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter className="gap-2">
                            <Button
                              variant="outline"
                              onClick={() => setOpenDelete(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => handleDelete(service)}
                            >
                              Delete
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog> */}

                      <Dialog
                        open={openDelete}
                        onOpenChange={(open) => {
                          setOpenDelete(open);
                          if (open) {
                            setServiceToDelete(service); // Set the service when opening
                          } else {
                            setServiceToDelete(null); // Clear when closing
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <DeleteIcon
                            className="text-red-500 size-5 cursor-pointer hover:text-red-700"
                          // onClick={(e) => {
                          //   e.stopPropagation(); // Prevent row click events
                          //   setServiceToDelete(service); // Set the service immediately
                          // }}
                          />
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Confirm Deletion</DialogTitle>
                            <DialogDescription>
                              Are you sure you want to delete {serviceToDelete?.serviceName}? This
                              action cannot be undone.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter className="gap-2">
                            <Button
                              variant="outline"
                              onClick={() => setOpenDelete(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              className="flex items-center justify-center gap-2"
                              onClick={handleDelete} // No need to pass service now
                            >
                              {isSubmitting ? <>
                              <Spinner/>
                              Deleting...
                              </>:"Delete"}
                              
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  {searchTerm
                    ? "No services match your search"
                    : "No services found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {filteredServices.length > ITEMS_PER_PAGE && (
        <div className="mt-6">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    currentPage > 1 && handlePageChange(currentPage - 1)
                  }
                  disabled={currentPage === 1}
                />
              </PaginationItem>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      isActive={currentPage === pageNum}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    currentPage < totalPages &&
                    handlePageChange(currentPage + 1)
                  }
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Service;
